/**
 * Solid Slate design tokens.
 *
 * The DARK values are read from docs/2017-6-27-SolidSlate_walkthrough.pdf, which
 * is the reference for the app's visual language.
 *
 * The LIGHT values are a DERIVATION, not a reference: the walkthrough is
 * dark-only, so no light-mode source exists. Light surfaces are inverted from
 * the dark scale and the status ramp is darkened to hold contrast on white.
 * Treat light mode as provisional until it has been reviewed against a real
 * light-mode design.
 */

/**
 * The status ramp encodes the booking workflow, so a colour always means the
 * same stage wherever it appears — a calendar cell border, a hold chip, or the
 * accent bar on a detail panel.
 *
 *   neutral   held, nothing pending
 *   attention selected, or a decision is being forced
 *   inFlight  sent and awaiting the other side (offer sent / accepted)
 *   ready     advance complete, show is production-ready
 *   settled   terminal, money reconciled
 */
export interface StatusRamp {
  neutral: string;
  attention: string;
  inFlight: string;
  ready: string;
  settled: string;
}

/** Surface scale, darkest recess to lightest raised element. */
export interface SurfaceScale {
  /** Page background, behind all panels. */
  canvas: string;
  /** Panels, cards, top bar. */
  surface: string;
  /** Raised details inside a panel: icon circles, inputs. */
  surfaceRaised: string;
  /** Recessed blocks: the logo cell, a selected calendar day. */
  inset: string;
  /** 1px hairlines and calendar grid lines. */
  border: string;
  /** Emphasised borders and dividers. */
  borderStrong: string;
  /** Meaningful input/control boundaries; stronger than decorative hairlines. */
  controlBorder: string;
}

export interface TextScale {
  primary: string;
  secondary: string;
  disabled: string;
  link: string;
}

export interface ModeTokens {
  surfaces: SurfaceScale;
  text: TextScale;
  status: StatusRamp;
  statusText: StatusRamp;
}

export const darkTokens: ModeTokens = {
  surfaces: {
    canvas: '#141416',
    surface: '#2E3238',
    surfaceRaised: '#3A3F46',
    inset: '#0D0D0F',
    border: '#4A4F56',
    borderStrong: '#6A7078',
    controlBorder: '#A4ABB4',
  },
  text: {
    primary: '#FFFFFF',
    // Chosen to clear WCAG AA (4.5:1) on every surface in the scale, including
    // the lighter raised greys: 7.98:1 on surface, 5.28:1 on a raised panel,
    // 11.39:1 on canvas. The earlier #9BA1A8 only managed 3.27:1 on raised
    // panels, which is what made labels muddy on the lighter grey blocks.
    secondary: '#C7CCD2',
    disabled: '#9299A2',
    link: '#B9DFFF',
  },
  statusText: {
    neutral: '#FFFFFF',
    attention: '#FFB4A9',
    inFlight: '#FFD28A',
    ready: '#9CEBAD',
    settled: '#B9DFFF',
  },
  status: {
    neutral: '#FFFFFF',
    attention: '#E8412C',
    inFlight: '#F5A623',
    ready: '#3DDC5C',
    settled: '#2B9FF5',
  },
};

export const lightTokens: ModeTokens = {
  surfaces: {
    canvas: '#F4F5F7',
    surface: '#FFFFFF',
    surfaceRaised: '#EDEFF2',
    inset: '#E4E7EB',
    border: '#D2D6DC',
    borderStrong: '#A8AEB6',
    controlBorder: '#737B85',
  },
  text: {
    primary: '#14171A',
    secondary: '#5A6169',
    disabled: '#737B85',
    link: '#0759A8',
  },
  // Darkened so each stage stays legible as text on a white surface.
  statusText: {
    neutral: '#1A1D21',
    attention: '#A21F1F',
    inFlight: '#805000',
    ready: '#17672A',
    settled: '#0759A8',
  },
  status: {
    neutral: '#1A1D21',
    attention: '#C62828',
    inFlight: '#B26A00',
    ready: '#1B7F32',
    settled: '#0B6BCB',
  },
};

export const fontStack = [
  'Roboto',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

/**
 * Recurring type treatments from the walkthrough. Wide tracking on small
 * uppercase labels is what carries the look, so these are tokens rather than
 * per-component sx values.
 */
export const typeTokens = {
  /** SOLID//SLATE lockup and other display lettering. */
  wordmark: {
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
  /** "TICKET SALES", "SEE DETAILS" — small, tracked, uppercase. */
  microLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.4,
  },
  /** "OFFER SENT", "READY FOR SHOW" — coloured text, never a filled pill. */
  statusChip: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
  },
  /** The large figures in the KPI row. */
  statValue: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.1,
  },
} as const;

/** Square panels and badges, with a softer shape for action buttons. */
export const shapeTokens = {
  borderRadius: 0,
  buttonBorderRadius: 6,
  /** Height of the status-coloured bar across the top of a panel. */
  accentBarHeight: 3,
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    status: StatusRamp;
    statusText: StatusRamp;
    readable: TextScale;
    surfaces: SurfaceScale;
  }
  interface PaletteOptions {
    status?: StatusRamp;
    statusText?: StatusRamp;
    readable?: TextScale;
    surfaces?: SurfaceScale;
  }
}
